/**
 * DoserneKunder — admin directory of every customer derived from
 * `doserne_projects`. Lets admins spot which customers already have
 * an active portal link, generate/rotate one in place, and copy the
 * shareable URL without leaving the page.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Users, Search, ExternalLink, Copy, RefreshCw, Share2,
  Trash2, CheckCircle2, Building2, Mail, Phone, Clock, X, Pencil,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtExpiry(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("no-NO", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function DoserneKunder() {
  const { user, loading, authHeader } = useDoserneAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({}); // { [customer_key]: bool }
  const [copiedKey, setCopiedKey] = useState("");
  const [editing, setEditing] = useState(null); // { key, value }
  const [savingSlug, setSavingSlug] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/customers`, authHeader());
      setRows(data.customers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste kunder");
    }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s) ||
      (c.phone || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const generate = async (c) => {
    if (!c.first_project_id) return toast.error("Kunden mangler et prosjekt");
    setBusy((b) => ({ ...b, [c.key]: true }));
    try {
      await axios.post(
        `${API}/doserne/projects/${c.first_project_id}/client-portal-customer`,
        { ttl_days: 30 },
        authHeader(),
      );
      toast.success(c.portal ? "Ny lenke generert" : "Lenke opprettet");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke generere lenke");
    }
    setBusy((b) => ({ ...b, [c.key]: false }));
  };

  const revoke = async (c) => {
    if (!c.first_project_id) return;
    if (!window.confirm(`Sikker på at du vil tilbakekalle lenken til ${c.name}?`)) return;
    setBusy((b) => ({ ...b, [c.key]: true }));
    try {
      await axios.delete(
        `${API}/doserne/projects/${c.first_project_id}/client-portal-customer`,
        authHeader(),
      );
      toast.info("Lenke tilbakekalt");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke tilbakekalle");
    }
    setBusy((b) => ({ ...b, [c.key]: false }));
  };

  const copy = async (c) => {
    if (!c.portal) return;
    const url = `${window.location.origin}${c.portal.url_path}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(c.key);
      setTimeout(() => setCopiedKey(""), 1800);
      toast.success("Kopiert");
    } catch {
      toast.error("Kopiering mislyktes");
    }
  };

  const saveSlug = async (c) => {
    if (!c.first_project_id) return;
    setSavingSlug(true);
    try {
      await axios.patch(
        `${API}/doserne/projects/${c.first_project_id}/client-portal-customer/slug`,
        { slug: editing.value || "" },
        authHeader(),
      );
      toast.success("Lenke oppdatert");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere lenke");
    }
    setSavingSlug(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;
  if (!user.is_admin) return <Navigate to="/doserne" replace />;

  return (
    <DoserneShell>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-black">Katalog</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight" data-testid="kunder-title">Kunder</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
          <Users className="w-4 h-4" />
          {rows.length}
        </div>
      </div>

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Søk etter navn, e-post eller telefon…"
          className="w-full pl-9 pr-9 py-2.5 border border-slate-300 rounded-lg text-sm bg-white"
          data-testid="kunder-search"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
            title="Tøm søk"
            data-testid="kunder-search-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic">
          {rows.length === 0 ? "Ingen kunder registrert på noen prosjekter enda." : "Ingen kunder matcher søket."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <div
              key={c.key}
              className={`bg-white border-2 rounded-2xl p-4 ${c.portal ? "border-cyan-200" : "border-slate-200"}`}
              data-testid={`kunder-row-${c.key}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${c.portal ? "bg-cyan-100 text-cyan-700" : "bg-slate-100 text-slate-600"}`}>
                <Building2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black text-slate-900 truncate">{c.name || "(uten navn)"}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                  {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                  <span className="inline-flex items-center gap-1">
                    <span className="font-mono font-bold text-slate-700">{c.active_projects}/{c.projects}</span> aktive prosjekter
                  </span>
                  {c.portal && (
                    <span className="inline-flex items-center gap-1 text-cyan-700 font-semibold">
                      <Clock className="w-3 h-3" /> til {fmtExpiry(c.portal.expires_at)}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 flex-wrap justify-end">
                {c.portal ? (
                  <>
                    <button
                      onClick={() => copy(c)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-xs font-black"
                      style={{ color: "#fff" }}
                      data-testid={`kunder-copy-${c.key}`}
                    >
                      <Copy className="w-3.5 h-3.5" /> {copiedKey === c.key ? "Kopiert!" : "Kopier"}
                    </button>
                    <a
                      href={c.portal.url_path}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-100 text-xs font-semibold text-slate-700"
                      data-testid={`kunder-open-${c.key}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button
                      onClick={() => setEditing({ key: c.key, value: c.portal.slug || "" })}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-100 text-xs font-semibold text-slate-700"
                      title="Rediger lenke"
                      data-testid={`kunder-edit-slug-${c.key}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => generate(c)}
                      disabled={busy[c.key]}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-cyan-300 hover:bg-cyan-50 text-cyan-700 text-xs font-semibold disabled:opacity-50"
                      title="Regenerer"
                      data-testid={`kunder-rotate-${c.key}`}
                    >
                      {busy[c.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => revoke(c)}
                      disabled={busy[c.key]}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-300 hover:bg-red-50 text-red-700 text-xs font-semibold disabled:opacity-50"
                      title="Tilbakekall"
                      data-testid={`kunder-revoke-${c.key}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => generate(c)}
                    disabled={busy[c.key]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-xs font-black disabled:opacity-50"
                    style={{ color: "#fff" }}
                    data-testid={`kunder-generate-${c.key}`}
                  >
                    {busy[c.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                    Generer lenke
                  </button>
                )}
              </div>
              </div>
              {editing?.key === c.key && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-500 font-mono flex-shrink-0">.../klient/c/</span>
                  <input
                    value={editing.value}
                    onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                    placeholder="tilfeldig-token-om-tom"
                    autoFocus
                    className="flex-1 min-w-[200px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono"
                    data-testid={`kunder-slug-input-${c.key}`}
                  />
                  <button
                    onClick={() => saveSlug(c)}
                    disabled={savingSlug}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-black disabled:opacity-50"
                    style={{ color: "#fff" }}
                    data-testid={`kunder-slug-save-${c.key}`}
                  >
                    {savingSlug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Lagre
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-100 text-xs font-semibold"
                  >
                    Avbryt
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </DoserneShell>
  );
}
